// Cart-page quantity editor for the die-cut sticker product.
//
// Scope: quantity only. Size is read from the line and passed back through
// unchanged — changing it here would leave the baked `_PRINT` artwork sized
// for the old dimensions, which is a wizard-side pipeline job, not a cart one.
//
// How it works, and why it works this way:
//
// Easify's own "Edit options" dialog is opened off-screen, its three
// .po-dimension-inputField inputs (Quantity, Width, Height, in that DOM
// order) are filled the same way js/easify.js drives Easify's product-page
// fields, and then — crucially — Easify's own recalculated properties are
// read straight out of its dialog <form> and written to the cart by us.
//
// Easify's Confirm button is deliberately NOT used. It ignores every form of
// synthetic click (verified live 16 Aug 2026: plain .click(), a full
// pointerover/pointerdown/mousedown/pointerup/mouseup/click sequence, and
// form.requestSubmit() — the first two changed nothing at all, the third fell
// through to a native GET navigation; the same edit by hand worked instantly).
// It evidently requires a genuinely trusted user gesture, which no script can
// forge. Don't spend time retrying this.
//
// What makes reading the form safe is that Easify recalculates everything
// itself once the fields change, so the form carries its own authoritative
// display string and `_po:items:addon` price. An earlier version hand-built
// those values instead and got them subtly wrong, once leaving a real cart
// line at £0. Never go back to computing them here — window.calculateStickerPrice
// is used *only* for the panel's on-screen preview and is never sent to the cart.
//
// The line's existing properties are fetched from /cart.js rather than
// rendered into the page. Liquid's `item.properties | json` emits an array of
// [name, value] pairs (not an object) and `escape` mangles the nested JSON
// into &quot; entities — merging from that wrote properties literally named
// "0", "1", "2" onto real cart lines. /cart.js returns them clean.

const STICKER_QUANTITIES = [10, 25, 50, 100, 200];
const MAX_STICKER_QUANTITY = 200;
const acceptedUpsells = window.stickerCartAcceptedUpsells || (window.stickerCartAcceptedUpsells = new Map());

const DISPLAY_PATTERN = /Quantity:\s*(\d+)\s*,\s*Width:\s*([\d.]+)\s*cm,\s*Height:\s*([\d.]+)\s*cm/i;

function formatDimension(value) {
  return Number(value).toFixed(2).replace(/\.00$/, "").replace(/(\.\d)0$/, "$1");
}

function formatMoney(value) {
  return new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(value);
}

function renderStickerDisplay(element, display, properties = {}) {
  if (!element || typeof display !== "string") return;
  const match = display.match(DISPLAY_PATTERN);
  if (!match) return;

  const rows = [
    ["Quantity", match[1]],
    ["Width", `${match[2]} cm`],
    ["Height", `${match[3]} cm`],
  ];
  const fragment = document.createDocumentFragment();
  if (properties["Bonus stickers"]) {
    rows.unshift(...["Total stickers", "Base amount", "Bonus stickers"].map((key) => [key, properties[key]]));
  }
  rows.forEach(([label, value]) => {
    const row = document.createElement("div");
    row.className = "product-option";
    const term = document.createElement("dt");
    term.textContent = `${label}:`;
    const description = document.createElement("dd");
    description.textContent = value;
    row.append(term, description);
    fragment.append(row);
  });
  element.replaceChildren(fragment);
}

function createStickerPropertyRow(label, value) {
  const row = document.createElement("div");
  row.className = "product-option";
  const term = document.createElement("dt");
  term.textContent = `${label}:`;
  const description = document.createElement("dd");
  description.textContent = value;
  row.append(term, description);
  return row;
}

// Shopify can briefly (or, on some cached theme renders, persistently) serve
// the cart section's legacy property markup. Normalise that markup in the
// browser as well, so the customer-facing result does not depend on Liquid
// having already added our data attributes.
function normaliseStickerCartRows() {
  document.querySelectorAll(".cart-item").forEach((cartItem) => {
    if (cartItem.dataset.stickerCartNormalised === "true") return;

    const titleLink = cartItem.querySelector(".cart-item__name");
    const title = titleLink?.textContent.trim().toLowerCase();
    const productHref = titleLink?.getAttribute("href") || "";
    const details = cartItem.querySelector(".cart-item__details");
    const detailsText = details?.textContent || "";
    const isSticker = title === "custom die cut vinyl sticker"
      || title === "custom die-cut vinyl sticker"
      || productHref.includes("/products/die-cut-vinyl-sticker")
      || (detailsText.includes("Quantity Custom") && detailsText.includes("Wizard state"));
    if (!isSticker || !details) return;

    const list = details.querySelector("dl");
    const displayMatch = detailsText.match(DISPLAY_PATTERN);
    if (!list || !displayMatch) return;

    const propertyValue = (wantedLabel) => {
      const row = [...list.querySelectorAll(".product-option")].find((option) =>
        option.querySelector("dt")?.textContent.trim().replace(/:$/, "").toLowerCase() === wantedLabel
      );
      return row?.querySelector("dd")?.textContent.trim() || "";
    };
    const finish = propertyValue("finish");
    const cutProfile = propertyValue("cut profile");

    if (titleLink) titleLink.textContent = "Custom Die-Cut Vinyl Sticker";

    const cleanRows = document.createDocumentFragment();
    if (finish) cleanRows.append(createStickerPropertyRow("Finish", finish));
    if (cutProfile) cleanRows.append(createStickerPropertyRow("Cut profile", cutProfile));

    const specifications = document.createElement("div");
    specifications.className = "sticker-cart-specs";
    specifications.dataset.stickerDisplay = "";
    const founderProperties = Object.fromEntries(
      ["Total stickers", "Base amount", "Bonus stickers"]
        .map((key) => [key, propertyValue(key.toLowerCase())])
    );
    renderStickerDisplay(specifications, displayMatch[0], founderProperties);
    cleanRows.append(specifications);

    list.replaceChildren(cleanRows);
    cartItem.dataset.stickerCartNormalised = "true";
  });
}

async function updateFreeShippingBar(cart) {
  const bar = document.querySelector("[data-free-shipping-bar]");
  if (!bar) return;

  let total = Number(cart?.total_price);
  try {
    const latestCart = await fetch(`${routes.cart_url}.js`, { headers: { Accept: "application/json" } })
      .then((response) => response.ok ? response.json() : null);
    if (Number.isFinite(Number(latestCart?.total_price))) total = Number(latestCart.total_price);
  } catch {
    // The cart-change response remains a safe fallback if this refresh fails.
  }

  const threshold = Number(bar?.dataset.freeShippingThreshold);
  if (!Number.isFinite(total) || !Number.isFinite(threshold) || threshold <= 0) return;

  const remaining = Math.max(0, threshold - total);
  const progress = Math.min(100, Math.max(0, (total / threshold) * 100));
  const message = bar.querySelector("[data-free-shipping-message]");
  const fill = bar.querySelector("[data-free-shipping-fill]");

  if (fill) {
    fill.style.setProperty("--free-shipping-progress", String(progress));
    fill.style.width = `${progress}%`;
  }
  bar.classList.toggle("free-shipping-bar--complete", remaining === 0);
  if (!message) return;

  message.classList.toggle("free-shipping-bar__message--complete", remaining === 0);
  message.innerHTML = remaining === 0
    ? "You've unlocked <strong>free shipping</strong>!"
    : `You're <strong>${formatMoney(remaining / 100)}</strong> away from <strong>free shipping</strong> on orders over ${formatMoney(threshold / 100)}!`;
}

function setEasifyInputValue(field, value) {
  const nativeSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, "value")?.set;
  if (nativeSetter) nativeSetter.call(field, value);
  else field.value = value;
}

function dispatchEasifyFieldEvents(field) {
  field.dispatchEvent(new Event("input", { bubbles: true }));
  field.dispatchEvent(new Event("change", { bubbles: true }));
}

function waitFor(predicate, { timeout = 5000, interval = 100 } = {}) {
  return new Promise((resolve, reject) => {
    const startedAt = Date.now();
    (function poll() {
      let result = null;
      try {
        result = predicate();
      } catch {
        result = null;
      }
      if (result) return resolve(result);
      if (Date.now() - startedAt >= timeout) return reject(new Error("Timed out waiting for Easify."));
      setTimeout(poll, interval);
    })();
  });
}

class StickerCartEditor {
  constructor(panel) {
    this.panel = panel;
    this.cartItem = panel.closest(".cart-item");
    this.line = Number(panel.dataset.line);
    this.lineQuantity = Number(panel.dataset.lineQuantity);

    const match = panel.dataset.currentValue.match(DISPLAY_PATTERN);
    if (!match) throw new Error("Unrecognised Quantity Custom format on this line.");
    this.quantity = Number(match[1]);
    this.width = Number(match[2]);
    this.height = Number(match[3]);

    this.quantityButtons = [...panel.querySelectorAll("[data-sticker-quantity]")];
    this.customButton = panel.querySelector("[data-sticker-quantity-custom]");
    this.customRow = panel.querySelector("[data-sticker-custom-row]");
    this.customInput = panel.querySelector("[data-sticker-custom-input]");
    this.customConfirm = panel.querySelector("[data-sticker-custom-confirm]");
    this.errorElement = panel.querySelector("[data-sticker-error]");
    this.upsell = panel.querySelector("[data-sticker-upsell]");
    this.upsellQuantity = panel.querySelector("[data-sticker-upsell-quantity]");
    this.upsellPrice = panel.querySelector("[data-sticker-upsell-price]");
    this.upsellButton = panel.querySelector("[data-sticker-upsell-button]");
    this.upsellDismiss = panel.querySelector("[data-sticker-upsell-dismiss]");

    // Quantity saves itself on selection. `savedQuantity` is what the cart
    // currently holds, so a queued save knows whether there is still work to do.
    this.savedQuantity = this.quantity;
    this.saveTimer = null;
    this.saving = false;
    this.upsellRequested = false;

    this.quantityButtons.forEach((button) => {
      button.addEventListener("click", () => {
        this.customRow.hidden = true;
        this.upsellRequested = false;
        this.resetAcceptedUpsell();
        this.setQuantity(Number(button.dataset.stickerQuantity));
      });
    });

    this.upsellButton?.addEventListener("click", () => {
      const target = Number(this.upsellButton.dataset.stickerUpsellTarget);
      if (!target) return;
      acceptedUpsells.set(this.line, target);
      this.upsellRequested = false;
      this.customRow.hidden = true;
      this.customInput.value = target;
      this.setQuantity(target);
    });

    this.upsellDismiss?.addEventListener("click", () => {
      this.upsellRequested = false;
      this.renderUpsell();
    });

    this.customButton.addEventListener("click", () => {
      this.customRow.hidden = false;
      this.upsellRequested = false;
      this.customInput.value = this.quantity;
      this.customInput.focus();
      this.render();
    });

    // A typed quantity is only committed once it is finished — on blur or
    // Enter — never per keystroke. Typing "112" would otherwise be read as 1,
    // then 11, then 112, saving each in turn and flickering the row total
    // through prices the customer never asked for.
    //
    // Every listener here stops propagation: Dawn's <cart-items> listens for
    // `change` bubbling from anywhere inside it and assumes it came from its
    // own quantity stepper, reading dataset.min/max/step off whatever fired
    // it, so these events would otherwise be misread as a line-quantity edit
    // and race a second /cart/change.js call against ours.
    this.customInput.addEventListener("input", (event) => {
      event.stopPropagation();
      this.upsellRequested = false;
      this.resetAcceptedUpsell();
      this.renderUpsell();
    });
    this.customInput.addEventListener("change", (event) => {
      event.stopPropagation();
      this.commitCustomQuantity();
    });
    this.customInput.addEventListener("keydown", (event) => {
      if (event.key !== "Enter") return;
      // This panel sits inside the cart's own <form>, where Enter would
      // otherwise submit it and reload the page mid-edit.
      event.preventDefault();
      event.stopPropagation();
      this.commitCustomQuantity({ collapse: true });
    });

    // Keep focus in the field until click so the input's blur/change event
    // cannot race the explicit confirmation. The tick gives touch and mouse
    // users an obvious save action without requiring the keyboard's Enter key.
    this.customConfirm?.addEventListener("pointerdown", (event) => event.preventDefault());
    this.customConfirm?.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.commitCustomQuantity({ collapse: true });
    });

    // A saved custom quantity is represented by a normal compact pill. The
    // customer can click that numbered pill to reopen the editable field.
    if (!this.presetButtonFor(this.quantity)) {
      this.customRow.hidden = true;
      this.customInput.value = this.quantity;
    }

    this.render();
  }

  // Reads the typed quantity and commits it, held to the input's own bounds.
  //
  // Those bounds are the only ones there are: Easify renders its Quantity
  // field without min or max attributes, so the limits configured against the
  // option in its admin are never enforced on the page. A number outside them
  // is pulled to the nearest allowed value and explained, rather than snapping
  // back to the previous quantity, which just looks broken. Entries that
  // aren't a usable number at all revert quietly.
  commitCustomQuantity({ collapse = false } = {}) {
    const typed = Number(this.customInput.value);
    const min = Number(this.customInput.min) || 1;
    const max = Number(this.customInput.max) || Infinity;

    if (this.customInput.value.trim() === "" || !Number.isFinite(typed) || !Number.isInteger(typed)) {
      this.customInput.value = this.quantity;
      this.errorElement.style.display = "none";
      return;
    }

    const clamped = Math.min(Math.max(typed, min), max);
    if (clamped !== typed) {
      this.errorElement.textContent = `Quantities run from ${min} to ${max} — using ${clamped}.`;
      this.errorElement.style.display = "block";
    } else {
      this.errorElement.style.display = "none";
    }

    this.customInput.value = clamped;

    // Preset values always collapse to their existing button. Enter and the
    // tick explicitly confirm any other custom value, replacing the wide
    // input with a compact numbered pill and dismissing its upsell.
    if (this.presetButtonFor(clamped) || collapse) {
      this.customRow.hidden = true;
      this.upsellRequested = collapse && !this.presetButtonFor(clamped);
    }

    if (clamped === this.quantity) {
      this.render();
      return;
    }
    this.resetAcceptedUpsell();
    this.setQuantity(clamped);
  }

  setQuantity(quantity) {
    this.quantity = quantity;
    this.render();
    this.previewPrice();
    this.queueSave();
  }

  // Selections save themselves, but a save waits ~2s on Easify recalculating,
  // so firing one per click would let several overlap and land out of order —
  // the cart could settle on a quantity the customer passed through rather
  // than the one they stopped on. Typing in the custom box is worse still, as
  // "200" would otherwise save 2, then 20, then 200.
  //
  // So changes are debounced, and only ever one save runs at a time; whatever
  // the customer last chose is re-checked when it finishes, and saved then if
  // it still differs. The last selection always wins.
  queueSave() {
    window.clearTimeout(this.saveTimer);
    this.saveTimer = window.setTimeout(() => this.runQueuedSaves(), 500);
  }

  async runQueuedSaves() {
    if (this.saving) return; // The running save will pick up the newer value.
    this.saving = true;
    this.panel.setAttribute("aria-busy", "true");
    this.errorElement.style.display = "none";

    try {
      while (this.quantity !== this.savedQuantity) {
        const target = this.quantity;
        await this.saveQuantity(target);
        this.savedQuantity = target;
      }
    } catch {
      this.errorElement.textContent = "That change couldn't be saved. Try again or reopen this item in the builder.";
      this.errorElement.style.display = "block";
      // Leave savedQuantity alone: the cart still holds the old value, so a
      // later change knows there is still work to do.
    } finally {
      this.saving = false;
      this.panel.removeAttribute("aria-busy");
    }
  }

  presetButtonFor(quantity) {
    return this.quantityButtons.find((button) => Number(button.dataset.stickerQuantity) === quantity) || null;
  }

  render() {
    const usingCustom = !this.customRow.hidden;
    const preset = usingCustom ? null : this.presetButtonFor(this.quantity);
    this.quantityButtons.forEach((button) => {
      button.setAttribute("aria-pressed", String(button === preset));
    });
    const usingConfirmedCustom = !usingCustom && !preset;
    this.customButton.textContent = usingConfirmedCustom ? String(this.quantity) : "Custom";
    this.customButton.setAttribute("aria-pressed", String(usingCustom || usingConfirmedCustom));
    this.customButton.setAttribute(
      "aria-label",
      usingConfirmedCustom ? `Edit custom quantity ${this.quantity}` : "Enter a custom sticker quantity"
    );
    this.renderUpsell();
  }

  resetAcceptedUpsell() {
    acceptedUpsells.delete(this.line);
  }

  getUpsell(quantity = this.quantity) {
    if (!Number.isInteger(quantity) || quantity < 1 || quantity >= MAX_STICKER_QUANTITY) return null;
    if (acceptedUpsells.get(this.line) === quantity) return null;
    const target = Math.min(MAX_STICKER_QUANTITY, Math.ceil((quantity * 1.2) / 25) * 25);
    if (target <= quantity) return null;
    const currentPrice = window.calculateStickerPrice(quantity, this.width, this.height);
    const targetPrice = window.calculateStickerPrice(target, this.width, this.height);
    return {
      target,
      extraQuantity: target - quantity,
      extraPrice: Math.max(0, targetPrice - currentPrice),
    };
  }

  renderUpsell(quantity = this.quantity) {
    if (!this.upsell || !this.upsellButton) return;
    const offer = this.customRow.hidden && this.upsellRequested ? this.getUpsell(quantity) : null;
    this.upsell.hidden = !offer;
    if (!offer) return;
    this.upsellQuantity.textContent = String(offer.extraQuantity);
    this.upsellPrice.textContent = formatMoney(offer.extraPrice);
    this.upsellButton.dataset.stickerUpsellTarget = offer.target;
    this.upsellButton.setAttribute("aria-label", `Add ${offer.extraQuantity} more stickers and choose ${offer.target}`);
  }

  // Shows the pending price in the panel and in the row's own total column, so
  // the change is visible where the customer is already looking.
  //
  // This is an estimate from our own formula and can sit a few pence away from
  // what Easify ultimately charges, so it is only ever shown for a *pending*
  // edit — never after saving, where setSavedPrice puts Easify's real figure
  // back in both places.
  previewPrice() {
    this.setRowTotal(formatMoney(window.calculateStickerPrice(this.quantity, this.width, this.height)));
  }

  setRowTotal(text) {
    this.cartItem
      .querySelectorAll(".cart-item__price-wrapper > .price")
      .forEach((element) => {
        element.textContent = text;
      });
  }

  // `quantity` is passed in rather than read from `this` mid-flight: the
  // customer can click another option while this is still waiting on Easify,
  // and a save must finish writing the value it started with.
  async saveQuantity(quantity) {
    const existingProperties = await this.fetchLineProperties();
    const properties = await this.readRecalculatedProperties(existingProperties, quantity);
    const percentage = Math.max(0, Math.min(100, Number(this.panel.dataset.stickerOfferPercentage) || 0));
    const bonus = this.panel.dataset.stickerOfferEligible === "true" ? Math.ceil((quantity * percentage) / 100) : 0;
    ["Sticker offer", "Total stickers", "Base amount", "Bonus stickers", "Bonus stickers from Founders Offer"].forEach((key) => delete properties[key]);
    if (bonus) {
      properties["Sticker offer"] = this.panel.dataset.stickerOfferId || "founders-offer";
      properties["Total stickers"] = String(quantity + bonus);
      properties["Base amount"] = String(quantity);
      properties["Bonus stickers"] = String(bonus);
    }
    const cart = await this.saveProperties(properties);
    this.applySavedState(properties, cart);
  }

  async fetchLineProperties() {
    const cart = await fetch(`${routes.cart_url}.js`).then((response) => response.json());
    const item = cart.items[this.line - 1];
    if (!item) throw new Error("This cart line no longer exists.");
    return item.properties || {};
  }

  // Opens Easify's dialog off-screen, types the chosen quantity into it, and
  // returns the properties Easify itself recalculated — merged over the ones
  // already on the line so nothing else (artwork especially) is lost.
  async readRecalculatedProperties(existingProperties, quantity) {
    document.documentElement.classList.add("sticker-edit-busy");
    try {
      const trigger = this.cartItem.querySelector(".po-editOptionsDialog-label");
      if (!trigger) throw new Error("Easify's edit trigger was not found on this line.");
      trigger.click();

      const dimensionFields = await waitFor(() => {
        const fields = [...document.querySelectorAll("dialog.po-dialog-dialog[open] .po-dimension-inputField")];
        return fields.length >= 3 ? fields : null;
      });

      const values = [String(quantity), formatDimension(this.width), formatDimension(this.height)];
      dimensionFields.slice(0, 3).forEach((field, index) => {
        setEasifyInputValue(field, values[index]);
        dispatchEasifyFieldEvents(field);
      });

      // Easify rebuilds the display string and the price (`_po:items:addon`,
      // which is what actually gets charged) in separate passes — observed
      // live with the string already showing a new £49.87 while the addon
      // still held the previous £8.25, and taking ~2s to settle. Reading at
      // that moment would save the new quantity against the old price.
      //
      // So this waits for both: the string matches the values just typed, and
      // the rounded price inside it agrees with the full-precision one in the
      // addon. That agreement is what proves both halves came from this edit.
      const expectedPrefix = `Quantity: ${quantity} , Width: ${values[1]} cm, Height: ${values[2]} cm`;
      const formData = await waitFor(() => {
        const form = document.querySelector("dialog.po-dialog-dialog[open] form");
        if (!form) return null;
        const data = new FormData(form);
        const display = data.get("properties[Quantity Custom]");
        if (typeof display !== "string" || !display.startsWith(expectedPrefix)) return null;

        const displayPrice = Number(display.match(/£\s*([\d.]+)/)?.[1]);
        const addonPrice = readAddonPrice(data.get("properties[_po:items:addon]"));
        if (!Number.isFinite(displayPrice) || !Number.isFinite(addonPrice)) return null;
        return Math.abs(displayPrice - addonPrice) < 0.005 ? data : null;
      }, { timeout: 15000 });

      return mergeEasifyProperties(existingProperties, formData);
    } finally {
      closeEasifyDialog();
      document.documentElement.classList.remove("sticker-edit-busy");
    }
  }

  // Asks /cart/change.js to re-render the cart sections alongside the update,
  // the same way Dawn's own quantity stepper does. Refreshing only the items
  // section leaves the footer's "Estimated total" showing the pre-edit figure.
  //
  // main-cart-items is deliberately NOT among them — see getSectionsToRender.
  async saveProperties(properties) {
    const sections = getSectionsToRender();
    const body = JSON.stringify({
      line: this.line,
      quantity: this.lineQuantity,
      properties,
      sections: sections.map((section) => section.section),
      sections_url: window.location.pathname,
    });

    const response = await fetch(routes.cart_change_url, { ...fetchConfig(), ...{ body } });
    const result = await response.json();
    if (result.errors) throw new Error(typeof result.errors === "string" ? result.errors : "Cart update rejected.");

    applyRenderedSections(sections, result.sections);
    return result;
  }

  // Updates this row from the saved result without re-rendering it, so the
  // Easify trigger inside it survives for the next edit.
  applySavedState(savedProperties, cart) {
    const display = savedProperties["Quantity Custom"];
    if (typeof display === "string") {
      this.panel.dataset.currentValue = display;
      const displayElement = this.cartItem.querySelector("[data-sticker-display]");
      renderStickerDisplay(displayElement, display, savedProperties);
    }

    const item = cart?.items?.[this.line - 1];
    if (item && Number.isFinite(item.final_line_price)) {
      this.setRowTotal(formatMoney(item.final_line_price / 100));
    }

    updateFreeShippingBar(cart);
    this.render();
  }
}

// Mirrors Dawn's CartItems.getSectionsToRender(), minus main-cart-items.
//
// Re-rendering the items section throws away the "Edit options" trigger Easify
// injects into each row with its own JS after page load — Easify never puts it
// back, so the next edit finds no trigger and fails ("worked once, then the
// buttons did nothing", 16 Aug 2026). The edited row is updated in place by
// applySavedState instead, which leaves Easify's DOM untouched.
function getSectionsToRender() {
  return [
    { id: "main-cart-footer", section: document.getElementById("main-cart-footer")?.dataset.id, selector: ".js-contents" },
    { id: "cart-icon-bubble", section: "cart-icon-bubble", selector: ".shopify-section" },
  ].filter((section) => section.section);
}

function applyRenderedSections(sections, rendered) {
  if (!rendered) return;
  sections.forEach(({ id, section, selector }) => {
    const html = rendered[section];
    const container = document.getElementById(id);
    if (!html || !container) return;
    const target = container.querySelector(selector) || container;
    const source = new DOMParser().parseFromString(html, "text/html").querySelector(selector);
    if (source) target.innerHTML = source.innerHTML;
  });
}

// Overlays the `properties[...]` fields Easify recalculated onto the ones
// already on the line. `_po:items:properties` is Easify's own JSON record of
// every option on the line, so it is merged key-by-key rather than swapped
// wholesale — the dialog's copy only describes the options it manages, and
// replacing it outright would drop an uploaded artwork's entry from it.
function mergeEasifyProperties(existingProperties, formData) {
  const merged = { ...existingProperties };

  for (const [key, value] of formData.entries()) {
    if (typeof value !== "string") continue; // Skip the dialog's empty file input.
    const match = key.match(/^properties\[(.+)\]$/);
    if (!match) continue;
    const name = match[1];

    if (name === "_po:items:properties") {
      merged[name] = JSON.stringify({
        ...safeParseObject(existingProperties[name]),
        ...safeParseObject(value),
      });
    } else {
      merged[name] = value;
    }
  }

  return merged;
}

// `_po:items:addon` is a doubly-nested array, e.g.
// [[{"price":49.86597406657539,"variantQty":1,"properties":[…]}]]
function readAddonPrice(raw) {
  if (typeof raw !== "string") return NaN;
  try {
    return Number(JSON.parse(raw)?.[0]?.[0]?.price);
  } catch {
    return NaN;
  }
}

function safeParseObject(raw) {
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function closeEasifyDialog() {
  const dialog = document.querySelector("dialog.po-dialog-dialog[open]");
  if (!dialog) return;
  const closeButton = dialog.querySelector('button[class*="closeButton"]');
  if (closeButton) closeButton.click();
  if (dialog.open) dialog.close();
}

const RESUMED_LINE_STORAGE_KEY = "stickerWizardReplacedLine";

// Finishes the swap started when a customer reopened a cart line in the wizard
// (js/resume.js in the Sticker-Wizard project). The wizard can't do this
// itself: Add to cart submits the theme's product form and navigates here,
// killing anything still running on that page. It leaves the line key in
// sessionStorage instead and this removes it once the replacement is visible.
//
// Removal only happens when the cart has actually grown since the wizard
// handed off. If it hasn't, the original is left exactly where it is — a
// duplicate the customer can delete beats deleting the only copy of an order
// whose replacement never made it.
async function completeResumedLineRemoval() {
  let pending = null;
  try {
    pending = JSON.parse(window.sessionStorage.getItem(RESUMED_LINE_STORAGE_KEY) || "null");
  } catch {
    return;
  }
  if (!pending?.key) return;

  const forget = () => {
    try {
      window.sessionStorage.removeItem(RESUMED_LINE_STORAGE_KEY);
    } catch {
      /* nothing useful to do */
    }
  };

  const cart = await fetch(`${routes.cart_url}.js`, { headers: { Accept: "application/json" } })
    .then((response) => response.json())
    .catch(() => null);
  // Couldn't read the cart — leave the marker so the next load can retry.
  if (!cart) return;

  const index = cart.items.findIndex((item) => item.key === pending.key);
  if (index === -1) {
    forget(); // Already gone.
    return;
  }
  if (!(cart.item_count > pending.itemCountBeforeAdd)) {
    forget(); // The replacement never arrived; keep what the customer has.
    return;
  }

  const body = JSON.stringify({ line: index + 1, quantity: 0 });
  const removed = await fetch(routes.cart_change_url, { ...fetchConfig(), ...{ body } })
    .then((response) => response.ok)
    .catch(() => false);

  forget();
  // Cleared first, so a failure here can never leave a reload loop behind.
  if (removed) {
    window.location.reload();
    return true;
  }
  return false;
}

const STICKER_OFFER_PROPERTY_KEYS = [
  "Sticker offer",
  "Total stickers",
  "Base amount",
  "Bonus stickers",
];

// Easify builds the original cart request from its own option state and can
// omit unrelated hidden inputs from the product form. Repair the resulting
// line in Shopify's cart itself, where the line properties are authoritative,
// before the customer proceeds to checkout and Flow reads the order.
async function ensureStickerOfferCartProperties() {
  const offerPanel = document.querySelector('[data-sticker-editor][data-sticker-offer-eligible="true"]');
  if (!offerPanel) return false;
  const offerId = offerPanel.dataset.stickerOfferId || "founders-offer";
  const percentage = Math.max(0, Math.min(100, Number(offerPanel.dataset.stickerOfferPercentage) || 0));

  let cart = await fetch(`${routes.cart_url}.js`, { headers: { Accept: "application/json" } })
    .then((response) => response.ok ? response.json() : null)
    .catch(() => null);
  if (!cart?.items) return false;

  let changed = false;
  for (let index = 0; index < cart.items.length; index++) {
    const item = cart.items[index];
    const properties = { ...(item.properties || {}) };
    const match = typeof properties["Quantity Custom"] === "string"
      ? properties["Quantity Custom"].match(DISPLAY_PATTERN)
      : null;
    if (!match) continue;

    const base = Number(match[1]);
    const bonus = Math.ceil((base * percentage) / 100);
    const expected = [offerId, String(base + bonus), String(base), String(bonus)];
    if (STICKER_OFFER_PROPERTY_KEYS.every((key, keyIndex) => String(properties[key] || "") === expected[keyIndex])) continue;

    STICKER_OFFER_PROPERTY_KEYS.forEach((key, keyIndex) => { properties[key] = expected[keyIndex]; });
    const response = await fetch(routes.cart_change_url, {
      ...fetchConfig(),
      body: JSON.stringify({ line: index + 1, quantity: item.quantity, properties }),
    });
    if (!response.ok) throw new Error("Shopify rejected the sticker offer cart details.");
    cart = await response.json();
    changed = true;
  }

  if (changed) window.location.reload();
  return changed;
}

function initStickerCartEditors() {
  normaliseStickerCartRows();
  document.querySelectorAll("[data-sticker-editor]").forEach((panel) => {
    if (panel.dataset.stickerEditorInitialised) return;
    panel.dataset.stickerEditorInitialised = "true";
    try {
      new StickerCartEditor(panel);
    } catch {
      // A line whose format this doesn't recognise keeps Easify's own
      // "Edit options" link as its editor rather than showing a dead panel.
      panel.hidden = true;
    }
  });
}

document.addEventListener("DOMContentLoaded", async () => {
  initStickerCartEditors();
  try {
    const replacementReloading = await completeResumedLineRemoval();
    if (!replacementReloading) await ensureStickerOfferCartProperties();
  } catch (error) {
    console.error("[Sticker Wizard] Could not save sticker offer cart details:", error);
  }
});

// Every cart change — ours above, or Dawn's own quantity stepper — replaces
// the contents of #main-cart-items, with no public "done updating" event to
// hook, so freshly rendered panels have to be picked up by observation.
//
// This observes #main-cart-items itself, NOT the .js-contents inside it: a
// refresh replaces that inner element, and an observer bound to it is left
// watching a detached node. That is exactly what made the panel work only
// once — after the first update the new panel got no listeners and no price.
const mainCartItems = document.getElementById("main-cart-items");
if (mainCartItems) {
  new MutationObserver(initStickerCartEditors).observe(mainCartItems, { childList: true, subtree: true });
}

// PRICE MISMATCH NOTICE (19 Sep 2026). Each sticker line's charged price
// (before discounts) is checked against its size and quantity with the
// wizard's own formula. A line that doesn't match gets a notice and the
// checkout buttons are disabled.
//
// This is NOT the security: anyone able to alter a price can also skip this
// page (straight to /checkout) or switch this script off. The real guard is
// the order check (Sticker-Pricing-App/order-price-guard), which holds any
// such order before printing. This catches honest glitches (Easify once left
// a real line at £0) and tells anyone tampering that it will not stick.
const STICKER_PRODUCT_ID = 12175716581716;
const PRICE_NOTICE_TEXT =
  "This item's price doesn't match its size and quantity, so it can't be checked out as it is. " +
  "Please remove it and add it again from the builder. Orders placed with altered prices are cancelled.";
let priceCheckTimer = 0;

function stickerLineHasPriceMismatch(item) {
  if (item.product_id !== STICKER_PRODUCT_ID) return false;
  // Can't judge without the formula (its module failed to load): stay quiet
  // rather than block every genuine customer.
  if (typeof window.calculateStickerPrice !== "function") return false;
  const match = String(item.properties?.["Quantity Custom"] ?? "").match(DISPLAY_PATTERN);
  if (!match) return true;
  const expected = window.calculateStickerPrice(parseInt(match[1], 10), parseFloat(match[2]), parseFloat(match[3])) * item.quantity;
  const charged = item.original_line_price / 100;
  return !Number.isFinite(expected) || Math.abs(expected - charged) > 0.01 * Math.max(1, item.quantity);
}

function setCheckoutBlocked(blocked) {
  document.documentElement.classList.toggle("sticker-price-mismatch", blocked);
  document.querySelectorAll('[name="checkout"], #checkout, #CartDrawer-Checkout').forEach((button) => {
    if (blocked) {
      if (button.disabled && !button.dataset.stickerPriceBlocked) return; // Disabled by the theme; not ours.
      button.disabled = true;
      button.dataset.stickerPriceBlocked = "true";
    } else if (button.dataset.stickerPriceBlocked) {
      button.disabled = false;
      delete button.dataset.stickerPriceBlocked;
    }
  });
}

async function checkStickerCartPrices() {
  priceCheckTimer = 0;
  // Mid-save, Easify's two-pass recalculation can briefly disagree with
  // itself; judge the settled cart instead.
  if (document.documentElement.classList.contains("sticker-edit-busy") || document.querySelector("[data-sticker-editor][aria-busy]")) {
    schedulePriceCheck(800);
    return;
  }
  let cart;
  try {
    cart = await fetch("/cart.js", { cache: "no-store" }).then((response) => response.json());
  } catch {
    return;
  }
  let anyMismatch = false;
  cart.items.forEach((item, index) => {
    const row = document.getElementById(`CartItem-${index + 1}`);
    const mismatch = stickerLineHasPriceMismatch(item);
    anyMismatch ||= mismatch;
    const existing = row?.querySelector("[data-sticker-price-notice]");
    if (!mismatch) {
      existing?.remove();
      return;
    }
    if (existing || !row) return;
    const notice = document.createElement("p");
    notice.className = "sticker-price-notice";
    notice.dataset.stickerPriceNotice = "";
    notice.setAttribute("role", "alert");
    notice.textContent = PRICE_NOTICE_TEXT;
    (row.querySelector(".cart-item__details") || row).append(notice);
  });
  setCheckoutBlocked(anyMismatch);
}

function schedulePriceCheck(delay = 400) {
  window.clearTimeout(priceCheckTimer);
  priceCheckTimer = window.setTimeout(checkStickerCartPrices, delay);
}

document.addEventListener("DOMContentLoaded", () => schedulePriceCheck(0));
if (mainCartItems) {
  // Re-check after every cart re-render, but not after our own notices
  // appearing or going, which would loop.
  const isOwnNotice = (node) => node.nodeType === 1 && node.matches?.("[data-sticker-price-notice]");
  new MutationObserver((mutations) => {
    if (mutations.every((mutation) => [...mutation.addedNodes, ...mutation.removedNodes].every(isOwnNotice))) return;
    schedulePriceCheck();
  }).observe(mainCartItems, { childList: true, subtree: true });
}
